import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api, formatError } from "@/lib/api";
import { captureAndSaveLocation } from "@/lib/geo";
import { Heart, Phone as PhoneIcon, KeyRound, Loader2 } from "lucide-react";

export default function Auth() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const initialTab = sp.get("mode") === "register" ? "register" : sp.get("mode") === "login" ? "login" : sp.get("mode") === "email" ? "login" : "otp";
  const { login, register, user, refresh } = useAuth();
  const { t } = useLang();

  const [tab, setTab] = useState(initialTab);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const refFromUrl = (sp.get("ref") || "").trim();
  const [regData, setRegData] = useState({
    email: "",
    password: "",
    name: "",
    mobile: "",
    gender: "male",
    referral_code: refFromUrl,
  });
  const [busy, setBusy] = useState(false);

  // If a referral code is present in the URL, auto-switch to Register tab and
  // pre-fill the referral field. Also persist it so a page-refresh keeps it.
  useEffect(() => {
    if (refFromUrl) {
      setTab("register");
      setRegData((r) => ({ ...r, referral_code: refFromUrl }));
      try { localStorage.setItem("umg_ref", refFromUrl); } catch { /* ignore */ }
    } else {
      try {
        const stored = localStorage.getItem("umg_ref") || "";
        if (stored) setRegData((r) => ({ ...r, referral_code: stored }));
      } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refFromUrl]);

  // Note: post-auth navigation is handled inside doLogin/doRegister
  // to preserve intent (register → /profile/edit, admin login → /admin).

  const doLogin = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(loginData.email, loginData.password);
      toast.success(`Welcome back, ${u.name}`);
      nav(u.role === "admin" ? "/admin" : "/home");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const doRegister = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(regData);
      toast.success(`Welcome to Umaang, ${u.name}`);
      try { localStorage.removeItem("umg_ref"); } catch { /* ignore */ }
      // Silently attempt to capture browser location — powers "Near Me" 100km radius.
      // Fully optional: user can deny and everything still works via city/PIN fallback.
      captureAndSaveLocation().catch(() => {});
      nav("/profile/edit");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  // ---- Mobile OTP flow ----
  const [otpStep, setOtpStep] = useState(1);        // 1 = mobile input, 2 = code+name input
  const [otpMobile, setOtpMobile] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpName, setOtpName] = useState("");
  const [otpGender, setOtpGender] = useState("male");
  const [otpIsExisting, setOtpIsExisting] = useState(false);
  const [otpMasked, setOtpMasked] = useState("");
  const [resendIn, setResendIn] = useState(0);

  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  const otpSend = async (e) => {
    if (e) e.preventDefault();
    if (!otpMobile || otpMobile.replace(/\D/g, "").length < 10) {
      toast.error("Enter a valid 10-digit mobile number");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/otp/send", { mobile: otpMobile });
      setOtpMasked(data.mobile || otpMobile);
      setOtpIsExisting(!!data.is_existing_user);
      setOtpStep(2);
      setResendIn(30);
      toast.success(data.mocked ? "OTP sent (mock: use 123456)" : "OTP sent to " + (data.mobile || otpMobile));
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const otpVerify = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length < 4) { toast.error("Enter the OTP"); return; }
    if (!otpIsExisting && !otpName.trim()) { toast.error("Please tell us your name"); return; }
    setBusy(true);
    try {
      const refCode = refFromUrl || (typeof localStorage !== "undefined" ? localStorage.getItem("umg_ref") : "") || "";
      const { data } = await api.post("/auth/otp/login", {
        mobile: otpMasked || otpMobile,
        code: otpCode,
        name: otpName || undefined,
        gender: otpGender || undefined,
        referral_code: refCode || undefined,
      });
      localStorage.setItem("umg_token", data.token);
      await refresh();
      try { localStorage.removeItem("umg_ref"); } catch { /* ignore */ }
      captureAndSaveLocation().catch(() => {});
      toast.success(`Welcome ${data.user?.name || ""} 💐`);
      // Existing user → /home; brand-new signup → let them fill profile
      nav(otpIsExisting ? "/home" : "/profile/edit");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 py-12 grid md:grid-cols-2 gap-10 items-start relative overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute -top-20 -left-20 w-96 h-96 rounded-full bg-gradient-to-br from-[#FCA5A5] to-transparent blur-3xl opacity-70" />
      <div aria-hidden className="pointer-events-none absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-gradient-to-br from-[#DDD6FE] to-transparent blur-3xl opacity-70" />
      <div className="hidden md:block sticky top-24 relative">
        <img src="/logo-full.png" alt="Umaang" className="h-24 w-auto mb-2 -ml-1" draggable="false" />
        <div className="uppercase tracking-[0.28em] text-xs bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] bg-clip-text text-transparent font-semibold">A Rishtā Reimagined</div>
        <h1 className="font-display text-4xl md:text-5xl mt-4 leading-tight">
          A private circle for lifelong bonds.
        </h1>
        <p className="mt-4 text-[#57534E] leading-relaxed">
          Every profile is Aadhaar-verified. Every conversation stays clean. Every match is measured.
        </p>
        <div className="mt-10 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E07A6E] to-[#C85A53] grid place-items-center shadow-md shadow-[#C85A53]/40">
            <Heart className="w-4 h-4 text-white" fill="white" />
          </div>
          <span className="text-sm text-[#57534E]">10 days free · Then decide</span>
        </div>
        <div className="mt-8 grid grid-cols-3 gap-3">
          {[
            { c: "#C85A53", n: "50k+", l: "Trusted" },
            { c: "#059669", n: "KYC",  l: "Verified" },
            { c: "#B45309", n: "10k+", l: "Stories" },
          ].map((s) => (
            <div key={s.l} className="pl-3 border-l-2 relative" style={{ borderColor: s.c }}>
              <div className="font-display text-xl" style={{ color: s.c }}>{s.n}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#57534E] mt-0.5">{s.l}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white/90 backdrop-blur border border-[#D6D2C9] p-8 shadow-xl relative" style={{ borderRadius: 12 }}>
        {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
        <button
          data-testid="google-signin-btn"
          type="button"
          onClick={() => {
            const redirectUrl = window.location.origin + "/auth/callback";
            window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
          }}
          className="w-full inline-flex items-center justify-center gap-3 h-11 rounded-full border border-[#D6D2C9] bg-white hover:bg-[#EAE6DF] transition text-sm font-medium mb-6"
        >
          <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
            <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
            <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 15.2 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.4 6.3 14.7z"/>
            <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2C29.3 34.9 26.8 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.5 16.2 44 24 44z"/>
            <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.2 5.2C41.4 35.4 44 30.1 44 24c0-1.3-.1-2.4-.4-3.5z"/>
          </svg>
          Continue with Google
        </button>
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-[#D6D2C9]" /></div>
          <div className="relative flex justify-center text-[10px] uppercase tracking-widest">
            <span className="bg-white px-3 text-[#57534E]">or use mobile</span>
          </div>
        </div>
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 bg-[#EAE6DF] rounded-full p-1 mb-8">
            <TabsTrigger value="otp" data-testid="tab-otp" className="rounded-full text-xs sm:text-sm data-[state=active]:bg-gradient-to-r data-[state=active]:from-[#C85A53] data-[state=active]:to-[#DB2777] data-[state=active]:text-white">
              <PhoneIcon className="w-3.5 h-3.5 mr-1" /> Mobile OTP
            </TabsTrigger>
            <TabsTrigger value="login" data-testid="tab-login" className="rounded-full text-xs sm:text-sm">
              {t("login")}
            </TabsTrigger>
            <TabsTrigger value="register" data-testid="tab-register" className="rounded-full text-xs sm:text-sm">
              {t("register")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={doLogin} className="space-y-5">
              <div>
                <Label>{t("email")}</Label>
                <Input
                  data-testid="login-email"
                  type="email"
                  required
                  value={loginData.email}
                  onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                  className="mt-2 border-[#D6D2C9]"
                />
              </div>
              <div>
                <Label>{t("password")}</Label>
                <Input
                  data-testid="login-password"
                  type="password"
                  required
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className="mt-2 border-[#D6D2C9]"
                />
              </div>
              <Button
                data-testid="login-submit"
                type="submit"
                disabled={busy}
                className="w-full btn-pill bg-gradient-to-r from-[#C85A53] to-[#A94842] hover:opacity-90 text-white h-11 shadow-md shadow-[#C85A53]/30"
              >
                {busy ? "…" : t("login")}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <Link to="/forgot-password" data-testid="forgot-link" className="text-[#C85A53] hover:underline">
                  Forgot password?
                </Link>
              </div>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={doRegister} className="space-y-4">
              <div>
                <Label>{t("name")}</Label>
                <Input
                  data-testid="reg-name"
                  required
                  value={regData.name}
                  onChange={(e) => setRegData({ ...regData, name: e.target.value })}
                  className="mt-2 border-[#D6D2C9]"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("email")}</Label>
                  <Input
                    data-testid="reg-email"
                    type="email"
                    required
                    value={regData.email}
                    onChange={(e) => setRegData({ ...regData, email: e.target.value })}
                    className="mt-2 border-[#D6D2C9]"
                  />
                </div>
                <div>
                  <Label>{t("mobile")}</Label>
                  <Input
                    data-testid="reg-mobile"
                    required
                    value={regData.mobile}
                    onChange={(e) => setRegData({ ...regData, mobile: e.target.value })}
                    className="mt-2 border-[#D6D2C9]"
                  />
                </div>
              </div>
              <div>
                <Label>{t("password")}</Label>
                <Input
                  data-testid="reg-password"
                  type="password"
                  required
                  minLength={6}
                  value={regData.password}
                  onChange={(e) => setRegData({ ...regData, password: e.target.value })}
                  className="mt-2 border-[#D6D2C9]"
                />
              </div>
              <div>
                <Label>{t("gender")}</Label>
                <RadioGroup
                  value={regData.gender}
                  onValueChange={(v) => setRegData({ ...regData, gender: v })}
                  className="mt-2 flex gap-4"
                >
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="male" data-testid="reg-gender-male" /> {t("male")}
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="female" data-testid="reg-gender-female" /> {t("female")}
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <RadioGroupItem value="other" data-testid="reg-gender-other" /> {t("other")}
                  </label>
                </RadioGroup>
              </div>
              <div>
                <Label>{t("referral_code")}</Label>
                <Input
                  data-testid="reg-referral"
                  value={regData.referral_code}
                  onChange={(e) => setRegData({ ...regData, referral_code: e.target.value })}
                  className="mt-2 border-[#D6D2C9]"
                  placeholder="UMG…"
                />
              </div>
              <Button
                data-testid="reg-submit"
                type="submit"
                disabled={busy}
                className="w-full btn-pill bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] hover:opacity-90 text-white h-11 shadow-md shadow-[#C85A53]/30"
              >
                {busy ? "…" : t("register")}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="otp">
            {otpStep === 1 ? (
              <form onSubmit={otpSend} className="space-y-5" data-testid="otp-step-mobile">
                <div>
                  <Label>Mobile number</Label>
                  <div className="mt-2 flex items-center border border-[#D6D2C9] rounded-md overflow-hidden bg-white">
                    <span className="px-3 py-2 bg-[#EAE6DF] text-sm text-[#57534E] border-r border-[#D6D2C9]">+91</span>
                    <Input
                      data-testid="otp-mobile"
                      type="tel"
                      inputMode="numeric"
                      maxLength={10}
                      placeholder="10-digit mobile"
                      autoFocus
                      required
                      value={otpMobile}
                      onChange={(e) => setOtpMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                      className="border-0 focus-visible:ring-0"
                    />
                  </div>
                  <div className="text-[10px] text-[color:var(--umg-text-2)] mt-1.5">Ek 6-digit code aapke phone par bhejenge (Twilio mock: 123456).</div>
                </div>
                <Button
                  data-testid="otp-send-btn"
                  type="submit"
                  disabled={busy || otpMobile.length < 10}
                  className="w-full btn-pill bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] hover:opacity-90 text-white h-11 shadow-md shadow-[#DB2777]/30"
                >
                  {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Sending…</> : <><PhoneIcon className="w-4 h-4 mr-1.5" /> Send OTP</>}
                </Button>
              </form>
            ) : (
              <form onSubmit={otpVerify} className="space-y-5" data-testid="otp-step-verify">
                <div className="p-3 rounded-lg bg-[color:var(--umg-secondary)] border border-[color:var(--umg-border)] text-xs flex items-center justify-between gap-2">
                  <span>OTP sent to <b>{otpMasked}</b></span>
                  <button
                    type="button"
                    data-testid="otp-change-mobile"
                    onClick={() => { setOtpStep(1); setOtpCode(""); }}
                    className="text-[#C85A53] hover:underline text-xs"
                  >Change</button>
                </div>
                <div>
                  <Label>Enter OTP</Label>
                  <Input
                    data-testid="otp-code"
                    type="tel"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="6-digit code"
                    autoFocus
                    required
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    className="mt-2 tracking-[0.4em] text-center text-xl border-[#D6D2C9]"
                  />
                </div>
                {!otpIsExisting && (
                  <>
                    <div className="p-3 rounded-lg bg-gradient-to-br from-[#FEF3C7]/60 to-[#FCE7F3]/60 text-xs">
                      🎉 <b>New here?</b> Ek chhoti si detail — phir aap seedha profile bharein.
                    </div>
                    <div>
                      <Label>Your name</Label>
                      <Input
                        data-testid="otp-name"
                        type="text"
                        placeholder="Full name"
                        required={!otpIsExisting}
                        value={otpName}
                        onChange={(e) => setOtpName(e.target.value)}
                        className="mt-2 border-[#D6D2C9]"
                      />
                    </div>
                    <div>
                      <Label>Gender</Label>
                      <RadioGroup
                        value={otpGender}
                        onValueChange={setOtpGender}
                        className="mt-2 flex gap-4"
                      >
                        {["male", "female", "other"].map((g) => (
                          <label key={g} className="flex items-center gap-2 text-sm cursor-pointer">
                            <RadioGroupItem value={g} data-testid={`otp-gender-${g}`} /> {t(g)}
                          </label>
                        ))}
                      </RadioGroup>
                    </div>
                  </>
                )}
                <Button
                  data-testid="otp-verify-btn"
                  type="submit"
                  disabled={busy || otpCode.length < 4}
                  className="w-full btn-pill bg-gradient-to-r from-[#C85A53] via-[#DB2777] to-[#7C3AED] hover:opacity-90 text-white h-11 shadow-md shadow-[#DB2777]/30"
                >
                  {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Verifying…</> : <><KeyRound className="w-4 h-4 mr-1.5" /> {otpIsExisting ? "Login" : "Verify & Sign up"}</>}
                </Button>
                <button
                  type="button"
                  data-testid="otp-resend"
                  disabled={resendIn > 0 || busy}
                  onClick={() => otpSend()}
                  className="w-full text-xs text-[color:var(--umg-text-2)] hover:text-[#C85A53] disabled:opacity-50"
                >
                  {resendIn > 0 ? `Resend in ${resendIn}s` : "Didn't get it? Resend OTP"}
                </button>
              </form>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
